import { klarityExtensions, normalizeTiptap } from "@klarity/schema";
import { Extension, getSchema, Node } from "@tiptap/core";
import { Fragment, DOMParser as PMDOMParser, type Schema, Slice } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { getHost } from "./host";
import { dataUrlToFile, imageFiles, insertImages } from "./images";

const DATA_IMAGE = /^data:image\/(png|jpeg|webp|gif);base64,/i;

/** Clean pasted HTML in an inert document (no scripts run, nothing loads). */
export function cleanPastedHtml(html: string): { body: HTMLElement; dataImages: string[]; droppedImages: number } {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const dataImages: string[] = [];
  let droppedImages = 0;
  for (const img of Array.from(doc.querySelectorAll("img"))) {
    const src = img.getAttribute("src") ?? "";
    if (DATA_IMAGE.test(src)) dataImages.push(src);
    else droppedImages += 1;
    img.remove();
  }
  for (const el of Array.from(doc.querySelectorAll("style, script, meta, link, title"))) el.remove();
  // Google Docs wraps the whole clipboard in <b style="font-weight:normal">.
  for (const el of Array.from(doc.querySelectorAll("b, strong"))) {
    if (/font-weight\s*:\s*(normal|[1-4]00)\b/i.test(el.getAttribute("style") ?? ""))
      el.replaceWith(...Array.from(el.childNodes));
  }
  // Word and Google Docs express bold/italic as span styles.
  for (const el of Array.from(doc.querySelectorAll("span[style]"))) {
    const style = el.getAttribute("style") ?? "";
    const bold = /font-weight\s*:\s*(bold|[6-9]00)\b/i.test(style);
    const italic = /font-style\s*:\s*italic/i.test(style);
    el.removeAttribute("style");
    // The schema allows one mark per run; with <em> outermost the inner <strong> wins (bold over italic).
    for (const tag of [bold ? "strong" : null, italic ? "em" : null]) {
      if (!tag) continue;
      const wrap = doc.createElement(tag);
      wrap.append(...Array.from(el.childNodes));
      el.append(wrap);
    }
  }
  return { body: doc.body, dataImages, droppedImages };
}

// Paste-only nodes so ordinary HTML tables and <br> survive parsing; normalizeTiptap maps them to the C3 schema.
const LooseTable = Node.create({
  name: "table",
  group: "block",
  content: "tableRow+",
  parseHTML: () => [{ tag: "table", priority: 40 }],
});
const LooseRow = Node.create({
  name: "tableRow",
  content: "(tableCell | tableHeader)+",
  parseHTML: () => [{ tag: "tr", context: "table/", priority: 60 }],
});
const cellAttrs = () => ({
  colspan: { default: 1, parseHTML: (el: HTMLElement) => Number(el.getAttribute("colspan") ?? 1) || 1 },
});
const LooseCell = Node.create({
  name: "tableCell",
  content: "block+",
  addAttributes: cellAttrs,
  parseHTML: () => [{ tag: "td", context: "tableRow/", priority: 60 }],
});
const LooseHeader = Node.create({
  name: "tableHeader",
  content: "block+",
  addAttributes: cellAttrs,
  parseHTML: () => [{ tag: "th", context: "tableRow/", priority: 60 }],
});
const HardBreak = Node.create({ name: "hardBreak", group: "inline", inline: true, parseHTML: () => [{ tag: "br" }] });

let looseParser: PMDOMParser | null = null;
function parser(): PMDOMParser {
  looseParser ??= PMDOMParser.fromSchema(
    getSchema([...klarityExtensions, LooseTable, LooseRow, LooseCell, LooseHeader, HardBreak]),
  );
  return looseParser;
}

export function pastedHtmlToSlice(html: string, schema: Schema) {
  const { body, dataImages, droppedImages } = cleanPastedHtml(html);
  const loose = parser().parse(body).toJSON();
  const normalized = normalizeTiptap(loose);
  const fragment = Fragment.fromArray(normalized.content.map((n) => schema.nodeFromJSON(n)));
  return { slice: Slice.maxOpen(fragment), dataImages, droppedImages };
}

/** Chrome's "Copy image" puts an <img> with a remote src next to the image file: HTML with images and no text. */
function onlyImages(html: string): boolean {
  const { body, dataImages, droppedImages } = cleanPastedHtml(html);
  return dataImages.length + droppedImages > 0 && !body.textContent?.trim();
}

export const KlarityPaste = Extension.create({
  name: "klarityPaste",
  addProseMirrorPlugins() {
    const editor = this.editor;
    let lastHtml: string | null = null;
    return [
      new Plugin({
        key: new PluginKey("klarityPaste"),
        props: {
          // Runs before ProseMirror's own paste handling: HTML left from an earlier drop or paste is never reused.
          handleDOMEvents: {
            paste: () => {
              lastHtml = null;
              return false;
            },
          },
          // Called first in ProseMirror's paste path; keep the raw HTML for handlePaste.
          transformPastedHTML: (html) => {
            lastHtml = html;
            return html;
          },
          handlePaste: (view, event) => {
            if (editor.isDestroyed || !editor.isEditable) return false;
            // No transformPastedHTML call means a plain-text paste (Shift held): never read the HTML then.
            const html = lastHtml ?? "";
            lastHtml = null;
            const files = imageFiles(event.clipboardData);
            if (files.length > 0 && (!html || onlyImages(html))) {
              void insertImages(editor, files, null);
              return true;
            }
            if (!html || html.includes("data-pm-slice") || view.state.selection.$from.parent.type.spec.code)
              return false;
            const result = pastedHtmlToSlice(html, view.state.schema);
            view.dispatch(
              view.state.tr
                .replaceSelection(result.slice)
                .scrollIntoView()
                .setMeta("paste", true)
                .setMeta("uiEvent", "paste"),
            );
            if (result.droppedImages > 0) {
              const n = result.droppedImages;
              getHost(editor).notify(
                `${n} image${n === 1 ? "" : "s"} could not be pasted because ${n === 1 ? "it is" : "they are"} not on this device. Save ${n === 1 ? "it" : "them"} and upload ${n === 1 ? "it" : "them"} instead.`,
                "warning",
              );
            }
            const pastedFiles = result.dataImages.map((d) => dataUrlToFile(d)).filter((f): f is File => f !== null);
            if (pastedFiles.length > 0) void insertImages(editor, pastedFiles, null);
            return true;
          },
          handleDrop: (view, event, _slice, moved) => {
            lastHtml = null; // a drop parses its HTML through transformPastedHTML too
            if (moved || editor.isDestroyed || !editor.isEditable) return false;
            const files = imageFiles(event.dataTransfer);
            if (files.length === 0) return false;
            const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
            void insertImages(editor, files, at ? at.pos : null);
            return true;
          },
        },
      }),
    ];
  },
});
